import React, { useState, useEffect } from "react";
import apiHelper from "../../../../../util/ApiHelper";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  usePagination,
  useSortBy,
  useRowSelect,
  useTable,
  useGlobalFilter,
} from "react-table";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Paper from "@material-ui/core/Paper";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { CustomCheckbox } from "../../../../../util";
import { Link, useRouteMatch } from "react-router-dom";
import ActionsBtns from "../Components/ActionsBtns";
import SearchField from "../Components/SearchField";
import { toast } from "react-toastify";

const useStylesAll = makeStyles((theme) => ({
  pagination: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
  text: {
    textDecoration: "none",
    color: "#333333",
  },
}));

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot = {
      width,
      height,
    };

    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }

    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 6, paddingBottom: 6 }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const DraggableTableRow = ({
  snapshot,
  data,
  row,
  provided,
  className,
  text,
  orderingDisabled,
}) => {
  const match = useRouteMatch();
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          <DraggableTableCell isDragOccurring={isDragging} scope="row">
            {row.cells[0].render("Cell")}
          </DraggableTableCell>
          <DraggableTableCell isDragOccurring={isDragging} scope="row">
            <img
              src={data.image}
              alt={data.code}
              style={{ maxHeight: "30px", maxWidth: "30px" }}
            />
          </DraggableTableCell>
          {row.cells.map((cell) => {
            if ({ ...cell.getCellProps() }.key.includes("title")) {
              return (
                <DraggableTableCell isDragOccurring={isDragging} align="left">
                  <Link
                    className={text}
                    to={`${match.url}/edit/${data.id}`}
                    {...cell.getCellProps()}
                    style={{
                      color: "#306F9D",
                    }}
                  >
                    {cell.render("Cell")}
                  </Link>
                </DraggableTableCell>
              );
            } else {
              return null;
            }
          })}
          <TableCell
            style={{ paddingTop: 6, paddingBottom: 6, color: "#CCCCCC" }}
            {...(!orderingDisabled && { ...provided.dragHandleProps })}
          >
            <DragHandleIcon />
          </TableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

export default function TagsTable(props) {
  const {
    globalFilter,
    orderingDisabled,
    handleSearch,
    searchString,
    setSearchString,
    fetchData,
    data,
    setData,
    disabled
  } = props;
  const [isDragging, setIsDragging] = useState(false);

  const columns = React.useMemo(
    () => [
      {
        Header: "Image",
        accessor: "img",
        disableGlobalFilter: true,
      },
      {
        Header: "Title",
        accessor: "title",
      },
    ],
    []
  );
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...{...getToggleAllRowsSelectedProps(), disabled}} />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox {...{...row.getToggleRowSelectedProps(), disabled}} />
            </div>
          ),
        },

        ...columns,
        // The drag handle can be used to drag the table's rows individually
        {
          id: "dragHandle",
          Header: () => <></>,
        },
      ]);
    }
  );

  useEffect(() => {
    setGlobalFilter(globalFilter);
  }, [globalFilter]);

  const saveOrdering = async () => {
    try {
      // Create an object mapping tag IDs to their new sequence numbers
      const seqData = {};
      data.forEach((item, index) => {
        seqData[item.id] = index + 1;
      });

      await apiHelper.cinefan.bulkUpdateTagSequence(seqData);
      toast.dark("Your changes is saved.");
      fetchData();
    } catch (err) {
      console.log(err);
      toast.error("Failed to save sequence changes. Please try again.");
    }
  };

  const onBeforeDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(data, result.source.index, result.destination.index);

    setData(items);
  };

  const classes = useStylesAll();

  return (
    <>
      <Grid container spacing={2} justify="space-between">
        <Grid container item xs={12} justify="space-between">
          <Grid container item xs={7}>
            <ActionsBtns
              orderingDisabled={orderingDisabled}
              fetchData={fetchData}
              saveOrdering={saveOrdering}
              rows={rows}
              disabled={disabled}
            />
          </Grid>
          <Grid container item xs={4} justify="flex-end" alignItems="center">
            <SearchField
              onSearch={handleSearch}
              searchString={searchString}
              setSearchString={setSearchString}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box width="100%">
            <IsDraggingContext.Provider value={isDragging}>
              <Paper>
                <Table
                  aria-label="simple table"
                  component="ul"
                  {...getTableProps()}
                >
                  <TableHead component="div">
                    {headerGroups.map((headerGroup) => {
                      props.handleSorting(false);
                      return (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column) => {
                            if (column.isSorted) {
                              props.handleSorting(true);
                            }
                            return (
                              <TableCell
                                {...column.getHeaderProps(
                                  column.getSortByToggleProps()
                                )}
                                style={{
                                  paddingTop: 6,
                                  paddingBottom: 6,
                                  verticalAlign: "baseline",
                                }}
                              >
                                {column.render("Header")}
                                {column.id !== "selection" &&
                                column.id !== "dragHandle" ? (
                                  <TableSortLabel
                                    active={column.isSorted}
                                    // react-table has an unsorted state which is not treated here
                                    direction={
                                      column.isSortedDesc ? "desc" : "asc"
                                    }
                                  />
                                ) : null}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableHead>
                  <DragDropContext
                    onBeforeDragStart={onBeforeDragStart}
                    onDragEnd={onDragEnd}
                  >
                    <Droppable droppableId="droppable" type="QUESTIONS">
                      {(provided, snapshot) => (
                        <TableBody
                          component="div"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          {...getTableBodyProps()}
                        >
                          {rows.map((row, index) => {
                            prepareRow(row);
                            return (
                              <Draggable
                                key={row.id}
                                draggableId={row.id}
                                index={index}
                                isDragDisabled={disabled}
                              >
                                {(provided, snapshot) => (
                                  <DraggableTableRow
                                    key={row.id}
                                    data={row.original}
                                    row={row}
                                    provided={provided}
                                    snapshot={snapshot}
                                    text={classes.text}
                                    orderingDisabled={props.orderingDisabled}
                                  />
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Table>
              </Paper>
              <Grid container spacing={3} justify="space-between">
                <Grid container item xs={7}>
                  <ActionsBtns
                    orderingDisabled={orderingDisabled}
                    fetchData={fetchData}
                    saveOrdering={saveOrdering}
                    rows={rows}
                    disabled={disabled}
                  />
                </Grid>
              </Grid>
            </IsDraggingContext.Provider>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
